import { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIntl } from 'react-intl';
import { Alert } from 'react-bootstrap-v5';

interface PasswordExpiryWarning {
  days_remaining: number;
  expires_at: string;
  message: string;
  warning_threshold: number;
  change_password_url?: string;
}

/**
 * Component that displays a warning banner when user's password is about to expire.
 * Reads the custom claim from Auth0 token added by the Password Expiration Action.
 */
const PasswordExpiryAlert: FC = () => {
  const { user, isAuthenticated } = useAuth0();
  const intl = useIntl();
  const [warning, setWarning] = useState<PasswordExpiryWarning | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    // Check for password expiry warning in custom claims
    const expiryWarning = (user as any)['https://konfidency.com.br/password_expiry_warning'];
    
    if (expiryWarning && expiryWarning.days_remaining) {
      setWarning(expiryWarning);
      
      // Reset dismissal if it's a new warning (different days)
      const lastDismissedDays = sessionStorage.getItem('password_warning_dismissed_days');
      if (lastDismissedDays !== String(expiryWarning.days_remaining)) {
        setDismissed(false);
        sessionStorage.removeItem('password_warning_dismissed_days');
      }
    }
  }, [user, isAuthenticated]);

  const handleDismiss = () => {
    setDismissed(true);
    if (warning) {
      sessionStorage.setItem('password_warning_dismissed_days', String(warning.days_remaining));
    }
  };

  const handleChangePassword = () => {
    // Use URL from token if available, otherwise fallback to env var
    const changePasswordUrl = warning?.change_password_url || 
                               `https://${process.env.REACT_APP_AUTH0_DOMAIN}/login?screen_hint=reset`;
    window.location.href = changePasswordUrl;
  };

  if (!warning || dismissed) {
    return null;
  }

  // Determine alert variant based on urgency
  const getAlertVariant = (daysRemaining: number): 'warning' | 'danger' => {
    if (daysRemaining <= 7) return 'danger';
    return 'warning';
  };

  const variant = getAlertVariant(warning.days_remaining);

  return (
    <Alert 
      variant={variant} 
      dismissible 
      onClose={handleDismiss}
      className="mb-5"
    >
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <i className={`bi bi-exclamation-triangle-fill fs-2x me-4 text-${variant}`}></i>
          <div>
            <h5 className="mb-1">
              {intl.formatMessage({
                id: 'password.expiry.warning.title',
                defaultMessage: 'Sua senha está expirando',
              })}
            </h5>
            <p className="mb-0">
              {intl.formatMessage({
                id: 'password.expiry.warning.message',
                defaultMessage: 'Sua senha expira em {days} dia(s). Altere sua senha agora para evitar perda de acesso.',
              }, { days: warning.days_remaining })}
            </p>
          </div>
        </div>
        <button 
          className="btn btn-primary btn-sm ms-4 flex-shrink-0"
          onClick={handleChangePassword}
        >
          {intl.formatMessage({
            id: 'password.expiry.warning.button',
            defaultMessage: 'Alterar Senha',
          })}
        </button>
      </div>
    </Alert>
  );
};

export { PasswordExpiryAlert };
